import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const WAVE_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='80'%3E%3Cpath d='M0 40 Q150 18 300 40 Q450 62 600 40' fill='none' stroke='%23C9A96E' stroke-width='1.4' opacity='0.20'/%3E%3Cpath d='M0 65 Q150 43 300 65 Q450 87 600 65' fill='none' stroke='%23C9A96E' stroke-width='0.9' opacity='0.12'/%3E%3C/svg%3E")`;

/* ─── CARD: fixed 140px image + colored strip — UNTOUCHED ─── */
function CatCard({ cat, isCommunity, isCurrent, isOther, isLocked, isTrial1, isTrial2,
                   teamColor, cardBg, darkMode, session, isPremiumUser, onInfo, onHoverInfo, onHoverLeave, onClick,
                   onLike, onFavorite, isFavorited }) {
  const isTrial    = isTrial1 || isTrial2;
  const qCount     = cat.question_count ?? cat.total_questions ?? null;
  const stripColor = isCommunity ? "#7c3aed" : (cat.color || "#5B0E14");

  return (
    <div
      data-testid={`cat-card-${cat.id}`}
      onClick={onClick}
      style={{
        position:"relative",
        cursor: isLocked && !isCommunity ? "default" : "pointer",
        userSelect:"none",
        transition:"transform 0.20s, opacity 0.20s",
        transform: isCurrent ? "scale(1.04)" : isOther ? "scale(0.97)" : "scale(1)",
        opacity: isOther ? 0.45 : isLocked ? 0.78 : 1,
      }}
      onMouseEnter={e => { if (!isCurrent && !isOther) e.currentTarget.style.transform = "scale(1.02)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = isCurrent ? "scale(1.04)" : "scale(1)"; }}
    >
      {/* stars */}
      {isCurrent && <>
        <span style={{ position:"absolute", top:"-10px", right:"-4px", zIndex:20, color:"#F1E194", fontSize:"1rem", pointerEvents:"none", filter:"drop-shadow(0 0 4px #F1E194)" }}>✦</span>
        <span style={{ position:"absolute", bottom:"-8px", left:"-4px", zIndex:20, color:"#F1E194", fontSize:"0.65rem", pointerEvents:"none" }}>✦</span>
      </>}

      {/* clipped card */}
      <div style={{
        borderRadius:"18px",
        overflow:"hidden",
        border: isCurrent
          ? `3px solid ${teamColor}`
          : isTrial1 ? "2.5px solid #ef4444"
          : isTrial2 ? "2.5px solid #3b82f6"
          : `1.5px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(91,14,20,0.10)"}`,
        boxShadow: isCurrent
          ? `0 0 0 2px ${teamColor}28, 0 8px 26px ${teamColor}38`
          : "0 2px 12px rgba(0,0,0,0.10)",
        background: cardBg,
      }}>

        {/* image area — fixed 150px (~7% larger than original 140px) */}
        <div style={{ position:"relative", height:"150px", overflow:"hidden" }}>
          {/* fallback */}
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:`linear-gradient(135deg,${stripColor}22,${stripColor}08)` }}>
            <span style={{ fontSize:"3.2rem" }}>{cat.icon || (isCommunity ? "🏛️" : "🎯")}</span>
          </div>
          {/* real image */}
          {cat.image_url && (
            <img src={cat.image_url} alt={cat.name}
              style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }}
              onError={e => { e.target.style.display = "none"; }}
            />
          )}
          {/* lock */}
          {isLocked && (
            <div style={{ position:"absolute", inset:0, zIndex:12, background:"rgba(0,0,0,0.52)", backdropFilter:"blur(2px)", WebkitBackdropFilter:"blur(2px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"4px" }}>
              <Lock size={20} color="#F1E194" />
              <span style={{ color:"#F1E194", fontWeight:900, fontSize:"0.65rem", fontFamily:"Cairo,sans-serif" }}>Premium فقط</span>
            </div>
          )}
          {/* ⓘ top-left — larger hit area, hover tooltip on desktop */}
          <div
            onClick={e => { e.stopPropagation(); onInfo && onInfo(cat); }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(59,130,246,1)";
              e.currentTarget.style.transform = "scale(1.10)";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,255,255,0.35), 0 4px 14px rgba(37,99,235,0.70)";
              onHoverInfo && onHoverInfo(cat, e.currentTarget.getBoundingClientRect());
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "rgba(29,78,216,0.95)";
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 0 0 2px rgba(255,255,255,0.22), 0 3px 12px rgba(37,99,235,0.60)";
              onHoverLeave && onHoverLeave();
            }}
            style={{
              position:"absolute", top:"6px", left:"6px", zIndex:13,
              width:"36px", height:"36px", borderRadius:"50%",
              background:"rgba(29,78,216,0.95)",
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"#fff", fontWeight:900, fontSize:"1rem",
              boxShadow:"0 0 0 2px rgba(255,255,255,0.22), 0 3px 12px rgba(37,99,235,0.60)",
              cursor:"pointer",
              transition:"background 0.15s, transform 0.15s, box-shadow 0.15s",
            }}
          >
            ⓘ
          </div>
          {/* count badge top-right */}
          {(qCount !== null || isCommunity) && (
            <div style={{ position:"absolute", top:"7px", right:"7px", zIndex:13, padding:"2px 7px", borderRadius:"999px", background: isCommunity ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "linear-gradient(135deg,#dc2626,#ea580c)", color:"#fff", fontSize:"0.56rem", fontWeight:900, whiteSpace:"nowrap", boxShadow:"0 2px 6px rgba(0,0,0,0.25)", fontFamily:"Cairo,sans-serif" }}>
              {isCommunity ? `🎮 ${cat.plays || 0}` : `باقي ${qCount} سؤال`}
            </div>
          )}
          {/* trial badge */}
          {isTrial && !isPremiumUser && (
            <div style={{ position:"absolute", bottom:"6px", right:"6px", zIndex:13, padding:"2px 7px", borderRadius:"999px", fontSize:"0.56rem", fontWeight:900, background: isTrial1 ? "rgba(239,68,68,0.88)" : "rgba(59,130,246,0.88)", color:"#fff", fontFamily:"Cairo,sans-serif" }}>
              {isTrial1 ? session?.team1_name : session?.team2_name}
            </div>
          )}
          {/* checkmark */}
          {isCurrent && isPremiumUser && (
            <div style={{ position:"absolute", bottom:"7px", left:"7px", zIndex:13, width:"24px", height:"24px", borderRadius:"50%", background:teamColor, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:900, fontSize:"0.82rem", boxShadow:`0 0 12px ${teamColor}88` }}>✓</div>
          )}
        </div>

        {/* colored strip */}
        <div style={{ background: isCurrent ? teamColor : stripColor, padding:"8px 10px 10px", transition:"background 0.2s", minHeight:"46px", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div
            title={cat.name}
            style={{ color:"#fff", fontWeight:900, fontSize:"0.84rem", fontFamily:"Cairo,sans-serif", textAlign:"center", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden", lineHeight:1.35, width:"100%" }}
          >
            {cat.icon && !isCommunity && <span style={{ marginLeft:"4px" }}>{cat.icon}</span>}
            {cat.name}
          </div>
        </div>

        {/* Like / Favorite action bar — community cards only */}
        {isCommunity && (onLike || onFavorite) && (
          <div
            style={{ display:"flex", background:"rgba(0,0,0,0.28)", borderTop:"1px solid rgba(255,255,255,0.08)" }}
            onClick={e => e.stopPropagation()}
          >
            {onLike && (
              <button
                onClick={e => { e.stopPropagation(); onLike(); }}
                style={{
                  flex:1, padding:"5px 0", background:"none", border:"none", cursor:"pointer",
                  fontSize:"0.70rem", fontWeight:800, fontFamily:"Cairo,sans-serif",
                  color: cat.user_liked ? "#f43f5e" : "rgba(255,255,255,0.42)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:"3px",
                  transition:"color 0.15s",
                }}
              >
                <span style={{ fontSize:"0.75rem" }}>{cat.user_liked ? "❤️" : "🤍"}</span>
                <span>{cat.likes || 0}</span>
              </button>
            )}
            {onLike && onFavorite && <div style={{ width:"1px", background:"rgba(255,255,255,0.08)" }} />}
            {onFavorite && (
              <button
                onClick={e => { e.stopPropagation(); onFavorite(); }}
                style={{
                  flex:1, padding:"5px 0", background:"none", border:"none", cursor:"pointer",
                  fontSize:"0.70rem", fontWeight:800, fontFamily:"Cairo,sans-serif",
                  color: isFavorited ? "#fbbf24" : "rgba(255,255,255,0.42)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:"3px",
                  transition:"color 0.15s",
                }}
              >
                <span style={{ fontSize:"0.75rem" }}>{isFavorited ? "⭐" : "☆"}</span>
                <span style={{ fontSize:"0.60rem" }}>مفضلة</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CategorySelectPage() {
  const navigate = useNavigate();
  const { session, updateSession, currentUser, darkMode, refreshUser, userToken, isPremium, gameMode } = useGame();
  const isSolo = gameMode === "solo";

  const [categories, setCategories]         = useState([]);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [freeData, setFreeData]             = useState(null);
  const [selected, setSelected]             = useState({ team1: [], team2: [] });
  const [step, setStep]                     = useState(1);
  const [loading, setLoading]               = useState(false);
  const [activeFilter, setActiveFilter]     = useState("all");
  const groupRefs                           = useRef({});
  const [activeSource, setActiveSource]     = useState("standard");
  const [commCats, setCommCats]             = useState([]);
  const [commPage, setCommPage]             = useState(0);
  const [commHasMore, setCommHasMore]       = useState(true);
  const [commLoading, setCommLoading]       = useState(false);
  const [commSort, setCommSort]             = useState("most_played");
  const [favoriteIds, setFavoriteIds]       = useState(new Set());
  const [likingId, setLikingId]             = useState(null);
  const groupScrollRef                      = useRef(null); // for horizontal drag-scroll
  const loaderRef                           = useRef(null);
  const [infoModal, setInfoModal]           = useState(null); // null or category object
  const [hoverTooltip, setHoverTooltip]     = useState(null); // {cat, x, y}
  const hoverTimerRef                       = useRef(null);
  const [trialCats, setTrialCats]           = useState([]); // full trial-eligible cat pool (from /trial/eligible-categories)

  const isPremiumUser = isPremium;
  const NEEDED = isSolo ? 6 : 3;
  // Trial user = logged-in, no paid entitlement, free gift not yet used
  const _isTrialUser = !isPremiumUser && !currentUser?.free_game_used;
  // Full set of trial-eligible category IDs (from /trial/eligible-categories, NOT the 6 slot IDs)
  const _trialCatIds = new Set(trialCats.map(c => c.id));
  // Premium Community = active timed subscription (biweekly/monthly)
  // Authoritative Premium check — mirrors _is_premium_community() in backend exactly.
  // Premium → Community UNLIMITED (all public categories, no deduction).
  // 'premium' sub_type: perpetual admin → always True
  // 'paid' + credits_expires_at active: time-based (biweekly/monthly) → True while not expired
  // 'paid' + no credits_expires_at + game_credits > 0: credit-based (two_games) → True while credits remain
  // 'free' sub_type (incl. gift/grant accounts): always False — no Premium
  const _isPremiumCommunity = (() => {
    if (!currentUser) return false;
    if (currentUser.subscription_type === 'premium') return true;       // perpetual
    if ((currentUser.game_credits || 0) > 0) return true;              // any credits → open
    if (currentUser.subscription_type !== 'paid') return false;
    const exp = currentUser.credits_expires_at;
    return exp ? new Date(exp) > new Date() : false;                    // time-based active
  })();

  useEffect(() => {
    if (!session) { navigate("/setup"); return; }
    if (userToken) refreshUser();
    loadCategories();
  }, []); // eslint-disable-line

  const loadCommCats = useCallback(async (page, sort) => {
    if (commLoading) return;
    setCommLoading(true);
    try {
      const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
      const { data } = await axios.get(`${API}/community/discover`, {
        params: { sort: sort || "most_played", skip: page * 20, limit: 20 },
        headers,
      });
      const items = data.items || [];
      setCommCats(prev => page === 0 ? items : [...prev, ...items]);
      setCommHasMore(items.length === 20);
      setCommPage(page + 1);
    } catch { /**/ }
    finally { setCommLoading(false); }
  }, [commLoading, userToken]); // eslint-disable-line

  useEffect(() => {
    if (activeSource !== "community") return;
    setCommCats([]);
    setCommPage(0);
    setCommHasMore(true);
    if (commSort === "favorites") {
      if (!userToken) { setCommHasMore(false); return; }
      axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${userToken}` } })
        .then(r => {
          const commFavs = (r.data || []).filter(c => c._fav_type === "community");
          setCommCats(commFavs);
          setCommHasMore(false);
          setFavoriteIds(new Set(commFavs.map(c => c.id)));
        })
        .catch(() => {});
    } else {
      loadCommCats(0, commSort);
      // Load favorite IDs for button state
      if (userToken) {
        axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${userToken}` } })
          .then(r => setFavoriteIds(new Set((r.data || []).filter(c => c._fav_type === "community").map(c => c.id))))
          .catch(() => {});
      }
    }
  }, [activeSource, commSort]); // eslint-disable-line

  useEffect(() => {
    if (!loaderRef.current) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && commHasMore && !commLoading) loadCommCats(commPage, commSort);
    }, { threshold: 0.1 });
    obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [commHasMore, commLoading, commPage, loaderRef.current]); // eslint-disable-line

  // ── Auto-advance: Team 1 finishes → instantly switch to Team 2 ──
  // Applies to both paid and trial users; not SOLO (single player)
  useEffect(() => {
    if (step !== 1 || isSolo) return;
    if (selected.team1.length === NEEDED) {
      const t = setTimeout(() => setStep(2), 200);
      return () => clearTimeout(t);
    }
  }, [selected.team1.length]); // eslint-disable-line


  const loadCategories = async () => {
    const requests = [
      axios.get(`${API}/categories`),
      axios.get(`${API}/free-categories`),
      axios.get(`${API}/category-groups`),
    ];
    // For trial users: also fetch the full eligible trial category pool
    if (_isTrialUser) requests.push(axios.get(`${API}/trial/eligible-categories`));
    const results = await Promise.all(requests);
    const [allRes, freeRes, groupsRes, trialRes] = results;

    setCategories(allRes.data);
    setFreeData(freeRes.data);

    // For trial users: populate trialCats from the authoritative full pool.
    // This is ALL categories with trial_visible=true AND trial_ready=true from free_categories.
    // Does NOT require main-game visibility.
    if (_isTrialUser && trialRes) {
      setTrialCats(trialRes.data.categories || []);
    }

    // Sort so "عامّة" is always first regardless of order field
    const rawGroups = groupsRes.data || [];
    const sortedGroups = [...rawGroups].sort((a, b) => {
      if (a.name === "عامّة") return -1;
      if (b.name === "عامّة") return 1;
      return (a.order ?? 0) - (b.order ?? 0);
    });
    setCategoryGroups(sortedGroups);
    // Never auto-select — user must choose explicitly. Also reset step in case of stale navigation.
    setSelected({ team1: [], team2: [] });
    setStep(1);
  };

  const handleLike = useCallback(async (catId) => {
    if (!userToken || likingId === catId) return;
    setLikingId(catId);
    try {
      const { data } = await axios.post(
        `${API}/community/categories/${catId}/like`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      setCommCats(prev => prev.map(c => c.id === catId
        ? { ...c, user_liked: data.liked, likes: Math.max(0, (c.likes || 0) + (data.liked ? 1 : -1)) }
        : c
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "حدث خطأ");
    } finally {
      setLikingId(null);
    }
  }, [userToken, likingId]);

  const handleFavorite = useCallback(async (catId) => {
    if (!userToken) return;
    const isFav = favoriteIds.has(catId);
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/remove`, {
          data: { category_id: catId },
          headers: { Authorization: `Bearer ${userToken}` },
        });
        setFavoriteIds(prev => { const s = new Set(prev); s.delete(catId); return s; });
        if (commSort === "favorites") setCommCats(prev => prev.filter(c => c.id !== catId));
      } else {
        await axios.post(
          `${API}/favorites/add`,
          { category_id: catId, category_type: "community" },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
        setFavoriteIds(prev => new Set([...prev, catId]));
      }
    } catch {
      toast.error("حدث خطأ");
    }
  }, [userToken, favoriteIds, commSort]);

  // ── Drag-to-scroll for group pills on desktop ─────────────────────────────
  // Enables mouse-drag horizontal scrolling for the group navigation row.
  // hasMoved prevents an accidental click after a drag gesture.
  useEffect(() => {
    const el = groupScrollRef.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollStart = 0, hasMoved = false;

    const onDown = (e) => {
      isDown = true; hasMoved = false;
      startX = e.pageX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
    };
    const onUp = () => { isDown = false; el.style.cursor = ""; };
    const onMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 4) hasMoved = true;
      // In RTL the scroll direction is mirrored: dragging right increases scrollLeft
      el.scrollLeft = scrollStart - dx;
    };
    // Suppress click on pills after a drag (threshold: 4 px)
    const onClickCapture = (e) => { if (hasMoved) { e.stopPropagation(); hasMoved = false; } };

    el.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("mousemove", onMove);
    el.addEventListener("click", onClickCapture, true);
    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("mousemove", onMove);
      el.removeEventListener("click", onClickCapture, true);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategoryClick = (cat) => {
    // is_premium check: skip for trial users selecting a trial-eligible category
    if (cat.is_premium && !isPremiumUser && !(_isTrialUser && _trialCatIds.has(cat.id))) {
      toast.error("هذه الفئة متاحة للمشتركين فقط — اشترك في Premium للوصول إليها!", { duration: 3000, icon: "🔒" });
      return;
    }
    const team = step === 1 ? "team1" : "team2";
    const current = selected[team];
    if (current.includes(cat.id)) {
      setSelected(prev => ({ ...prev, [team]: prev[team].filter(id => id !== cat.id) }));
    } else {
      if (current.length >= NEEDED) { toast.error(isSolo ? `اختر ${NEEDED} فئات فقط` : `يمكنك اختيار ${NEEDED} فئات فقط لكل فريق`); return; }
      setSelected(prev => ({ ...prev, [team]: [...prev[team], cat.id] }));
    }
  };

  const handleConfirm = async () => {
    if (isSolo) {
      if (selected.team1.length < NEEDED) { toast.error(`اختر ${NEEDED} فئات`); return; }
      await startGame(selected.team1, []);
      return;
    }
    // Both premium and trial users use the same step-based flow
    if (step === 1) {
      if (selected.team1.length < NEEDED) { toast.error(`اختر ${NEEDED} فئات للفريق الأول`); return; }
      setStep(2); return;
    }
    // Step 2: re-validate BOTH teams before creating game
    if (selected.team1.length < NEEDED) { toast.error(`اختر ${NEEDED} فئات للفريق الأول`); return; }
    if (selected.team2.length < NEEDED) { toast.error(`اختر ${NEEDED} فئات للفريق الثاني`); return; }
    const overlap = selected.team1.filter(id => selected.team2.includes(id));
    if (overlap.length > 0) { toast.error("لا يمكن اختيار نفس الفئة للفريقين!"); return; }
    const totalUnique = new Set([...selected.team1, ...selected.team2]).size;
    if (totalUnique !== 6) { toast.error("يجب اختيار 6 فئات مختلفة بالضبط"); return; }
    await startGame(selected.team1, selected.team2);
  };

  const startGame = async (t1Cats, t2Cats) => {
    setLoading(true);
    try {
      const updated = await updateSession({ team1_categories: t1Cats, team2_categories: t2Cats });
      if (updated) {
        const _mode = isSolo ? "solo" : "normal";
        const _entitlement = _isTrialUser ? "trial" : "paid";
        trackEvent("category_play_committed", {
          game_id:     session?.id,
          mode:        _mode,
          entitlement: _entitlement,
          team1_count: t1Cats.length,
          team2_count: t2Cats.length,
        });
        trackEvent("game_created", {
          game_id:     session?.id,
          mode:        _mode,
          entitlement: _entitlement,
          team1_name:  session?.team1_name,
          team2_name:  session?.team2_name,
        });
        navigate("/prepare");
      } else {
        navigate("/");
      }
    } catch (err) {
      if (err?.response?.status === 402 || err?.response?.status === 403) {
        navigate("/pricing");
      } else {
        toast.error("حدث خطأ، حاول مرة أخرى");
      }
    }
    finally { setLoading(false); }
  };

  const team1Sel  = selected.team1;
  const team2Sel  = selected.team2;
  const activeSel = step === 1 ? team1Sel : team2Sel;
  const teamName  = step === 1 ? session?.team1_name : session?.team2_name;
  const teamColor = step === 1 ? "#ef4444" : "#3b82f6";
  const otherSel  = step === 1 ? team2Sel : team1Sel;

  /* theme */
  const pageBg   = darkMode ? "#120a06" : "#FDF6E3";
  const cardBg   = darkMode ? "#1e120a" : "#ffffff";
  const textMain = darkMode ? "#F1E194" : "#5B0E14";
  const textSub  = darkMode ? "#a08050" : "#7a4a2a";
  const divider  = darkMode ? "rgba(241,225,148,0.10)" : "rgba(91,14,20,0.08)";

  // Start is valid only when BOTH teams are complete (normal) or solo has 6.
  // Checked against all selections, not just the active team, to prevent 5-category starts.
  const _noOverlap = team1Sel.filter(id => team2Sel.includes(id)).length === 0;
  const canConfirm = isSolo
    ? team1Sel.length >= NEEDED
    : (team1Sel.length >= NEEDED && team2Sel.length >= NEEDED && _noOverlap);

  const visibleGroups = activeFilter === "all"
    ? categoryGroups
    : categoryGroups.filter(g => g.id === activeFilter);

  if (!session) return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#0a0502",
    }}>
      <div style={{ width: 40, height: 40, border: "3px solid rgba(241,225,148,0.15)", borderTopColor: "#F1E194", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes catsel-step-in{from{opacity:0;transform:scale(0.94)}to{opacity:1;transform:scale(1)}}[data-group-scroll]::-webkit-scrollbar{display:none}`}</style>
    </div>
  );

  const cardProps = (cat, isCommunity = false) => ({
    cat, isCommunity,
    isCurrent: activeSel.includes(cat.id),
    isOther:   otherSel.includes(cat.id),
    isLocked:  cat.is_premium && !isPremiumUser,
    // Ownership derived from ACTUAL selection, not slot config
    isTrial1:  team1Sel.includes(cat.id) && !activeSel.includes(cat.id),
    isTrial2:  team2Sel.includes(cat.id) && !activeSel.includes(cat.id),
    teamColor, cardBg, darkMode, session, isPremiumUser,
    onInfo: (c) => setInfoModal(c),
    onHoverInfo: (c, rect) => {
      clearTimeout(hoverTimerRef.current);
      setHoverTooltip({ cat: c, x: rect.left + rect.width / 2, y: rect.bottom + 8 });
    },
    onHoverLeave: () => {
      hoverTimerRef.current = setTimeout(() => setHoverTooltip(null), 120);
    },
    onClick: () => {
      if (isCommunity) {
        if (_isTrialUser) {
          // Trial user: only allow community cats that are trial-eligible
          if (!_trialCatIds.has(cat.id)) {
            toast.error("هذه الفئة غير متاحة في التجربة المجانية", { icon: "🎁" });
            return;
          }
          if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
          handleCategoryClick(cat); return;
        }
        // Premium Community: can play any eligible published category
        if (_isPremiumCommunity) {
          if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
          handleCategoryClick(cat); return;
        }
        // Free creator: can only play own published community categories
        const _isOwnCat = currentUser && cat.creator_id === currentUser.id;
        if (_isOwnCat) {
          if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
          handleCategoryClick(cat); return;
        }
        // Legacy two_games community credits
        const _hasLegacyComm = (currentUser?.community_game_credits || 0) > 0;
        if (_hasLegacyComm) {
          if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
          handleCategoryClick(cat); return;
        }
        // Locked — not own content, not premium community, no legacy credits
        toast.error("هذه من فئات مجتمع حُجّة 🌍 اشترك وافتح جميع فئات المجتمع بلا حدود.", { duration: 3500, icon: "🔒" });
        return;
      }
      // Standard category
      if (!isPremiumUser) {
        if (_isTrialUser) {
          // Trial user: only allow trial-eligible categories
          if (!_trialCatIds.has(cat.id)) return; // not in trial list — silently ignore
          if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
          handleCategoryClick(cat); return;
        }
        // No entitlement at all
        if (cat.is_premium) toast.error("هذه الفئة متاحة للمشتركين فقط!", { icon: "🔒" });
        return;
      }
      if (!isSolo && otherSel.includes(cat.id)) { toast.error("هذه الفئة مختارة للفريق الآخر"); return; }
      handleCategoryClick(cat);
    },
  });

  // For trial users: the display pool is trialCats (all eligible), not the main categories list
  const _displayCats = _isTrialUser ? trialCats : categories;
  /* groups that have categories in the current display pool */
  const populatedGroups = categoryGroups.filter(g =>
    _displayCats.some(c => c.group_id === g.id)
  );

  return (
    <div className="catsel-page" style={{
      minHeight:"100vh",
      display:"flex",
      flexDirection:"column",
      overflowX:"hidden",
      backgroundColor: pageBg,
      backgroundImage: darkMode ? "none" : WAVE_BG,
      backgroundRepeat:"repeat-x",
      backgroundSize:"600px 80px",
      fontFamily:"Cairo,Tajawal,sans-serif",
      direction:"rtl",
    }}>

      {/* ══════════════════════════════════════
          STICKY HEADER
      ══════════════════════════════════════ */}
      <div className="catsel-header" style={{
        position:"sticky", top:0, zIndex:30,
        borderBottom:`1px solid ${divider}`,
        background: darkMode ? "rgba(18,10,6,0.95)" : "rgba(253,246,227,0.95)",
        backdropFilter:"blur(16px)",
        WebkitBackdropFilter:"blur(16px)",
      }}>
        <div style={{ maxWidth:"960px", margin:"0 auto", padding:"18px 28px 14px" }}>

          {/* Title */}
          <h1 style={{
            margin:0, textAlign:"center",
            fontWeight:900,
            fontSize:"clamp(1.3rem,2.6vw,1.8rem)",
            color:textMain,
            fontFamily:"Cairo,sans-serif",
            letterSpacing:"-0.01em",
          }}>
            اختيار الفئات
          </h1>

          {/* Status pill */}
          {/* Trial badge — shown above the step indicator for trial users */}
          {_isTrialUser && freeData?.trial_enabled && !isSolo && (
            <div style={{ textAlign:"center", marginTop:"8px", fontSize:"0.78rem", fontWeight:700, color:textSub }}>
              🎁 مباراتك المجانية{" "}
              <span style={{ color:"#B8860B", cursor:"pointer", textDecoration:"underline" }} onClick={() => navigate("/pricing")}>اشترك لمزيد</span>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"center", marginTop:"8px" }}>
            {isSolo ? (
              <div style={{ display:"inline-flex", alignItems:"center", gap:"10px", padding:"7px 20px", borderRadius:"999px", background:"rgba(147,197,253,0.10)", border:"2px solid rgba(147,197,253,0.45)" }}>
                <span style={{ color:"rgba(147,197,253,0.9)", fontWeight:900, fontSize:"0.85rem" }}>🎯 اختر {NEEDED} فئات</span>
                <div style={{ display:"flex", gap:"5px" }}>
                  {Array.from({length:NEEDED}).map((_,i) => (
                    <div key={i} style={{ width:"10px", height:"10px", borderRadius:"50%", background: i<activeSel.length ? "rgba(147,197,253,0.9)" : "transparent", border:"2px solid rgba(147,197,253,0.6)", transition:"background 0.18s" }} />
                  ))}
                </div>
              </div>
            ) : (
              <div key={`step-${step}`} style={{ display:"inline-flex", alignItems:"center", gap:"10px", padding:"7px 20px", borderRadius:"999px", background: step===1 ? "rgba(239,68,68,0.10)" : "rgba(59,130,246,0.10)", border:`2px solid ${teamColor}`, animation:"catsel-step-in 0.22s ease" }}>
                <span style={{ color:teamColor, fontWeight:900, fontSize:"0.85rem" }}>{teamName} — اختر {NEEDED} فئات</span>
                <div style={{ display:"flex", gap:"5px" }}>
                  {Array.from({length:NEEDED}).map((_,i) => (
                    <div key={i} style={{ width:"10px", height:"10px", borderRadius:"50%", background: i<activeSel.length ? teamColor : "transparent", border:`2px solid ${teamColor}`, transition:"background 0.18s" }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Teams progress — all non-solo (premium + trial), clickable to switch editing team */}
          {!isSolo && (
            <div style={{ display:"flex", gap:"10px", marginTop:"14px" }}>
              {[1,2].map(t => {
                const tName  = t===1 ? session?.team1_name : session?.team2_name;
                const tColor = t===1 ? "#ef4444" : "#3b82f6";
                const tSel   = t===1 ? team1Sel : team2Sel;
                const active = step===t;
                return (
                  <div key={t} onClick={() => setStep(t)} style={{ flex:1, borderRadius:"14px", padding:"9px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", background: active ? `${tColor}0e` : (darkMode ? "rgba(0,0,0,0.22)" : "rgba(255,255,255,0.55)"), border:`2px solid ${active ? tColor : divider}`, transition:"all 0.22s", cursor:"pointer" }}>
                    <span style={{ color:tColor, fontWeight:900, fontSize:"0.84rem" }}>{tName}</span>
                    <div style={{ display:"flex", gap:"4px" }}>
                      {Array.from({length:NEEDED}).map((_,i) => (
                        <div key={i} style={{ width:"10px", height:"10px", borderRadius:"50%", background: i<tSel.length ? tColor : "transparent", border:`2px solid ${tColor}`, transition:"background 0.18s" }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Source tabs */}
          <div style={{ display:"flex", gap:"8px", marginTop:"14px" }}>
            {[
              { id:"standard",  label:"🎯 الفئات" },
              { id:"community", label:"🏛️ المجتمع" },
            ].map(tab => {
              const active = activeSource===tab.id;
              const ac = tab.id==="community" ? "#7c3aed" : "#5B0E14";
              return (
                <button key={tab.id} onClick={() => setActiveSource(tab.id)} style={{
                  flex:1, padding:"9px 0", borderRadius:"999px",
                  fontWeight:900, fontSize:"0.88rem", cursor:"pointer",
                  border:`2px solid ${active ? ac : divider}`,
                  background: active ? ac : (darkMode ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.60)"),
                  color: active ? "#fff" : textSub,
                  transition:"all 0.20s", fontFamily:"Cairo,sans-serif",
                  boxShadow: active ? `0 4px 14px ${ac}40` : "none",
                }}>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ── Community sort/filter tabs ── */}
          {activeSource==="community" && (
            <div style={{ display:"flex", gap:"8px", overflowX:"auto", padding:"10px 0 2px", scrollbarWidth:"none", msOverflowStyle:"none" }}>
              {[
                { id:"most_played", label:"🔥 الأكثر لعباً" },
                { id:"top_liked",   label:"❤️ الأكثر إعجاباً" },
                ...(userToken ? [{ id:"favorites", label:"⭐ مفضلتي" }] : []),
              ].map(tab => {
                const active = commSort === tab.id;
                return (
                  <button key={tab.id} onClick={() => setCommSort(tab.id)} style={{
                    flexShrink:0, padding:"6px 16px", borderRadius:"999px",
                    background: active ? "#7c3aed" : (darkMode ? "rgba(0,0,0,0.22)" : "rgba(255,255,255,0.70)"),
                    border:`1.5px solid ${active ? "#7c3aed" : divider}`,
                    color: active ? "#fff" : textSub,
                    fontWeight: active ? 900 : 600,
                    fontSize:"0.8rem", cursor:"pointer",
                    whiteSpace:"nowrap", fontFamily:"Cairo,sans-serif",
                    transition:"all 0.18s",
                    boxShadow: active ? "0 3px 12px rgba(124,58,237,0.32)" : "none",
                    transform: active ? "translateY(-1px)" : "translateY(0)",
                  }}>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Navigation chips — feel like real navigation ── */}
          {activeSource==="standard" && populatedGroups.length > 0 && (
            <div
              ref={groupScrollRef}
              data-group-scroll
              style={{
                display:"flex", gap:"8px", overflowX:"auto", overflowY:"hidden",
                width:"100%", minWidth:0,
                padding:"14px 0 6px",
                scrollbarWidth:"none", msOverflowStyle:"none",
                WebkitOverflowScrolling:"touch",
                touchAction:"pan-x",
                cursor:"grab",
                userSelect:"none",
              }}
            >
              {/* "All" chip */}
              {(() => {
                const isAll = activeFilter === "all";
                return (
                  <button
                    onClick={() => setActiveFilter("all")}
                    style={{
                      flexShrink:0, padding:"6px 18px", borderRadius:"999px",
                      background: isAll ? "#5B0E14" : (darkMode ? "rgba(0,0,0,0.22)" : "rgba(255,255,255,0.70)"),
                      border:`1.5px solid ${isAll ? "#5B0E14" : divider}`,
                      color: isAll ? "#F1E194" : textSub,
                      fontWeight: isAll ? 900 : 600,
                      fontSize:"0.8rem", cursor:"pointer",
                      whiteSpace:"nowrap", fontFamily:"Cairo,sans-serif",
                      transition:"all 0.18s",
                      boxShadow: isAll ? "0 3px 12px rgba(91,14,20,0.32)" : "none",
                      transform: isAll ? "translateY(-1px)" : "translateY(0)",
                    }}
                  >الكل</button>
                );
              })()}

              {/* Group chips */}
              {populatedGroups.map(g => {
                const isActive = activeFilter === g.id;
                const gc = g.color || "#5B0E14";
                return (
                  <button key={g.id}
                    onClick={(e) => {
                      setActiveFilter(g.id);
                      // Scroll this pill into view within the row (not the page)
                      e.currentTarget.scrollIntoView({ behavior:"smooth", block:"nearest", inline:"nearest" });
                      // Also scroll page to the group section
                      setTimeout(() => groupRefs.current[g.id]?.scrollIntoView({ behavior:"smooth", block:"start" }), 60);
                    }}
                    style={{
                      flexShrink:0, padding:"6px 18px", borderRadius:"999px",
                      background: isActive ? gc : (darkMode ? "rgba(0,0,0,0.22)" : `${gc}0f`),
                      border:`1.5px solid ${isActive ? gc : `${gc}50`}`,
                      color: isActive ? "#fff" : (darkMode ? (g.color||"#F1E194") : gc),
                      fontWeight: isActive ? 900 : 600,
                      fontSize:"0.8rem", cursor:"pointer",
                      whiteSpace:"nowrap", fontFamily:"Cairo,sans-serif",
                      transition:"all 0.18s",
                      boxShadow: isActive ? `0 3px 14px ${gc}45` : "none",
                      transform: isActive ? "translateY(-1px)" : "translateY(0)",
                    }}
                  >
                    {g.icon} {g.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          SCROLLABLE BODY
      ══════════════════════════════════════ */}
      <div className="catsel-body" style={{ flex:1, overflowY:"auto", scrollBehavior:"smooth", padding:"48px 0 56px" }}>
        <div style={{ maxWidth:"960px", margin:"0 auto", padding:"0 28px" }}>

          {/* ── COMMUNITY ── */}
          {activeSource==="community" && (
            <div>
              {commLoading && commCats.length===0 && (
                <div style={{ textAlign:"center", padding:"72px 0", color:textSub, fontSize:"0.9rem" }}>جاري التحميل...</div>
              )}
              <div style={{
                display:"grid",
                gridTemplateColumns:"repeat(auto-fill, minmax(190px, 1fr))",
                gap:"20px",
              }}>
                {/* Trial users: only see community categories that are in the trial-eligible pool */}
                {(_isTrialUser ? commCats.filter(c => _trialCatIds.has(c.id)) : commCats).map(cat => (
                  <CatCard
                    key={cat.id}
                    {...cardProps(cat, true)}
                    onLike={userToken ? (
                      _isPremiumCommunity
                        ? () => handleLike(cat.id)
                        : () => toast.error("الإعجابات من مزايا اشتراك حُجّة 🌍", { duration: 2500 })
                    ) : null}
                    onFavorite={userToken ? () => handleFavorite(cat.id) : null}
                    isFavorited={favoriteIds.has(cat.id)}
                  />
                ))}
              </div>
              {/* Empty state for favorites */}
              {!commLoading && commSort === "favorites" && commCats.length === 0 && (
                <div style={{ textAlign:"center", padding:"72px 0", color:textSub }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"12px" }}>⭐</div>
                  <div style={{ fontWeight:700, fontSize:"0.9rem" }}>لم تضف أي فئة للمفضلة بعد</div>
                  <div style={{ fontSize:"0.78rem", marginTop:"6px", opacity:0.7 }}>اضغط ☆ على أي فئة مجتمع لحفظها هنا</div>
                </div>
              )}
              <div ref={loaderRef} style={{ padding:"32px 0", textAlign:"center", color:textSub, fontSize:"0.78rem" }}>
                {commLoading && commCats.length>0 ? "جاري تحميل المزيد..." : commHasMore ? "" : commCats.length>0 && commSort !== "favorites" ? "لا توجد فئات إضافية" : ""}
              </div>
            </div>
          )}

          {/* ── STANDARD SECTIONS ── */}
          {activeSource==="standard" && (
            <div>
              {visibleGroups.map((g, index) => {
                // Use _displayCats — trial users see trialCats pool, paid users see all categories
                const groupCats = _displayCats.filter(c => c.group_id === g.id);
                if (!groupCats.length) return null;
                const gc = g.color || "#5B0E14";

                return (
                  <div
                    key={g.id}
                    ref={el => groupRefs.current[g.id]=el}
                    style={{
                      paddingTop: index === 0 ? "0" : "76px",
                      scrollMarginTop: "160px",
                    }}
                  >
                    {/* ── Chapter heading ── */}
                    <div style={{ marginBottom: "26px" }}>
                      {/* Header row */}
                      <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>

                        {/* Icon box */}
                        <div style={{
                          width:"52px", height:"52px",
                          borderRadius:"14px",
                          flexShrink:0,
                          background: darkMode ? `${gc}28` : `${gc}18`,
                          border:`2px solid ${gc}38`,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:"1.55rem",
                          boxShadow:`0 4px 20px ${gc}25`,
                        }}>
                          {g.icon || "🎯"}
                        </div>

                        {/* Title + count */}
                        <div style={{ flex:1 }}>
                          <div style={{
                            color: darkMode ? "#F1E194" : gc,
                            fontWeight:900,
                            fontSize:"clamp(1.1rem,2.2vw,1.35rem)",
                            fontFamily:"Cairo,sans-serif",
                            lineHeight:1.15,
                            letterSpacing:"-0.015em",
                          }}>
                            {g.name}
                          </div>
                          <div style={{
                            color:textSub,
                            fontSize:"0.72rem",
                            fontWeight:600,
                            marginTop:"3px",
                            fontFamily:"Cairo,sans-serif",
                          }}>
                            {groupCats.length} فئة متاحة
                          </div>
                        </div>
                      </div>

                      {/* Accent underline */}
                      <div style={{
                        marginTop:"14px",
                        height:"3px",
                        borderRadius:"2px",
                        background:`linear-gradient(to left, ${gc}, ${gc}00)`,
                      }} />
                    </div>

                    {/* Cards grid */}
                    <div style={{
                      display:"grid",
                      gridTemplateColumns:"repeat(auto-fill, minmax(190px, 1fr))",
                      gap:"20px",
                    }}>
                      {groupCats.map(cat => <CatCard key={cat.id} {...cardProps(cat)} />)}
                    </div>
                  </div>
                );
              })}

              {/* Ungrouped */}
              {activeFilter==="all" && (() => {
                const ungrouped = _displayCats.filter(c => !c.group_id);
                if (!ungrouped.length) return null;
                const isFirst = visibleGroups.filter(g => categories.some(c => c.group_id===g.id)).length === 0;
                return (
                  <div
                    ref={el => groupRefs.current["other"]=el}
                    style={{
                      paddingTop: isFirst ? "0" : "76px",
                      scrollMarginTop: "160px",
                    }}
                  >
                    {/* Chapter heading */}
                    <div style={{ marginBottom:"26px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>
                        <div style={{
                          width:"52px", height:"52px",
                          borderRadius:"14px",
                          flexShrink:0,
                          background: darkMode ? "rgba(91,14,20,0.28)" : "rgba(91,14,20,0.12)",
                          border:"2px solid rgba(91,14,20,0.25)",
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:"1.55rem",
                          boxShadow:"0 4px 20px rgba(91,14,20,0.18)",
                        }}>
                          🌐
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{
                            color: darkMode ? "#F1E194" : "#5B0E14",
                            fontWeight:900,
                            fontSize:"clamp(1.1rem,2.2vw,1.35rem)",
                            fontFamily:"Cairo,sans-serif",
                            lineHeight:1.15,
                            letterSpacing:"-0.015em",
                          }}>
                            عامّة
                          </div>
                          <div style={{ color:textSub, fontSize:"0.72rem", fontWeight:600, marginTop:"3px", fontFamily:"Cairo,sans-serif" }}>
                            {ungrouped.length} فئة متاحة
                          </div>
                        </div>
                      </div>
                      <div style={{
                        marginTop:"14px",
                        height:"3px",
                        borderRadius:"2px",
                        background:"linear-gradient(to left, #5B0E14, #5B0E1400)",
                      }} />
                    </div>

                    <div style={{
                      display:"grid",
                      gridTemplateColumns:"repeat(auto-fill, minmax(190px, 1fr))",
                      gap:"20px",
                    }}>
                      {ungrouped.map(cat => <CatCard key={cat.id} {...cardProps(cat)} />)}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          HOVER TOOLTIP (desktop only)
      ══════════════════════════════════════ */}
      {hoverTooltip && (
        <div
          onMouseEnter={() => clearTimeout(hoverTimerRef.current)}
          onMouseLeave={() => { hoverTimerRef.current = setTimeout(() => setHoverTooltip(null), 120); }}
          style={{
            position: "fixed",
            left: Math.min(hoverTooltip.x - 140, window.innerWidth - 296),
            top: Math.min(hoverTooltip.y, window.innerHeight - 140),
            zIndex: 9999,
            width: 280,
            background: darkMode ? "rgba(14,8,4,0.97)" : "#1a0507",
            border: `1px solid ${(hoverTooltip.cat.color || "#5B0E14")}55`,
            borderRadius: 14,
            padding: "12px 14px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.65)",
            pointerEvents: "auto",
            fontFamily: "Cairo,Tajawal,sans-serif",
            direction: "rtl",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: "0.82rem", color: "#F1E194", marginBottom: 6 }}>
            {hoverTooltip.cat.icon && <span style={{ marginLeft: 4 }}>{hoverTooltip.cat.icon}</span>}
            {hoverTooltip.cat.name}
          </div>
          <div style={{ fontSize: "0.78rem", lineHeight: 1.7, color: "rgba(255,255,255,0.72)" }}>
            {hoverTooltip.cat.description
              ? hoverTooltip.cat.description
              : <span style={{ color: "rgba(255,255,255,0.30)", fontStyle: "italic" }}>لا يوجد وصف متاح.</span>
            }
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          CATEGORY INFO MODAL
      ══════════════════════════════════════ */}
      {infoModal && (
        <div
          onClick={() => setInfoModal(null)}
          style={{
            position:"fixed", inset:0, zIndex:200,
            background:"rgba(0,0,0,0.78)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:"20px", fontFamily:"Cairo,Tajawal,sans-serif", direction:"rtl",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: darkMode ? "rgba(14,8,4,0.98)" : "#FDF6E3",
              border:`1.5px solid ${(infoModal.color||"#5B0E14")}40`,
              borderRadius:"22px",
              padding:"28px 28px 22px",
              maxWidth:"360px", width:"100%",
              boxShadow:"0 0 80px rgba(0,0,0,0.75), 0 0 40px rgba(91,14,20,0.22)",
            }}
          >
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", gap:"14px", marginBottom:"18px" }}>
              {infoModal.image_url ? (
                <img
                  src={infoModal.image_url} alt=""
                  style={{ width:"54px", height:"54px", borderRadius:"14px", objectFit:"cover", flexShrink:0, border:`2px solid ${(infoModal.color||"#5B0E14")}38` }}
                  onError={e => { e.target.style.display="none"; }}
                />
              ) : (
                <div style={{ width:"54px", height:"54px", borderRadius:"14px", flexShrink:0, background:`${infoModal.color||"#5B0E14"}28`, border:`2px solid ${(infoModal.color||"#5B0E14")}38`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.9rem" }}>
                  {infoModal.icon || "🎯"}
                </div>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:900, fontSize:"1rem", color: darkMode ? "#F1E194" : "#5B0E14", lineHeight:1.35, wordBreak:"break-word" }}>
                  {infoModal.name}
                </div>
                {(infoModal.question_count ?? infoModal.total_questions) != null && (
                  <div style={{ fontSize:"0.72rem", marginTop:"4px", fontWeight:700, color: darkMode ? "rgba(241,225,148,0.45)" : "rgba(91,14,20,0.50)" }}>
                    {infoModal.question_count ?? infoModal.total_questions} سؤال متاح
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div style={{
              fontSize:"0.88rem", lineHeight:1.8,
              color: darkMode ? "rgba(255,255,255,0.68)" : "rgba(91,14,20,0.82)",
              borderTop:`1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(91,14,20,0.10)"}`,
              paddingTop:"14px",
              minHeight:"48px",
            }}>
              {infoModal.description
                ? infoModal.description
                : <span style={{ color: darkMode ? "rgba(255,255,255,0.28)" : "rgba(91,14,20,0.32)", fontStyle:"italic", fontSize:"0.84rem" }}>لم تتم إضافة وصف لهذه الفئة بعد.</span>
              }
            </div>

            {/* Close */}
            <button
              onClick={() => setInfoModal(null)}
              style={{
                marginTop:"18px", width:"100%", padding:"10px 0",
                borderRadius:"999px", border:`1px solid ${darkMode ? "rgba(255,255,255,0.10)" : "rgba(91,14,20,0.14)"}`,
                background: darkMode ? "rgba(255,255,255,0.05)" : "rgba(91,14,20,0.06)",
                color: darkMode ? "rgba(255,255,255,0.50)" : "rgba(91,14,20,0.60)",
                fontWeight:700, fontSize:"0.85rem", cursor:"pointer",
                fontFamily:"Cairo,sans-serif", transition:"all 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          STICKY BOTTOM CTA
      ══════════════════════════════════════ */}
      <div style={{
        flexShrink:0,
        borderTop:`1px solid ${divider}`,
        background: darkMode ? "rgba(18,10,6,0.95)" : "rgba(253,246,227,0.97)",
        backdropFilter:"blur(16px)",
        WebkitBackdropFilter:"blur(16px)",
      }}>
        <div style={{ maxWidth:"960px", margin:"0 auto", padding:"14px 28px 22px" }}>
          {/* All non-solo users (premium + trial) share the same step-based buttons */}
          <div style={{ display:"flex", gap:"10px" }}>
            {!isSolo && step===2 && (
              <button onClick={() => setStep(1)} style={{
                flex:1, padding:"13px 0", borderRadius:"999px",
                fontWeight:900, fontSize:"1rem",
                background: darkMode ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.80)",
                color:textMain, border:`1.5px solid ${divider}`,
                cursor:"pointer", fontFamily:"Cairo,sans-serif",
                transition:"all 0.18s",
              }}>رجوع</button>
            )}
            <button
              data-testid="confirm-categories-btn"
              onClick={handleConfirm}
              disabled={loading || !canConfirm}
              style={{
                flex:1, padding:"13px 0", borderRadius:"999px",
                fontWeight:900, fontSize:"1rem",
                background: canConfirm ? (isSolo ? "linear-gradient(135deg,#93c5fd,#3b82f6)" : teamColor) : (darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.09)"),
                color: canConfirm ? "#fff" : textSub,
                border:"none", cursor: canConfirm ? "pointer" : "default",
                opacity: loading ? 0.65 : 1,
                transition:"all 0.22s", fontFamily:"Cairo,sans-serif",
                boxShadow: canConfirm ? (isSolo ? "0 4px 20px rgba(59,130,246,0.45)" : `0 4px 20px ${teamColor}55`) : "none",
              }}
            >
              {loading ? "جاري التحميل..." : isSolo ? "ابدأ التحدي 🎯" : step===1 ? `التالي — ${session?.team2_name} ←` : "ابدأ اللعبة!"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
